// ContextSelector (Parte 14.4/14.7): tenants de `contexts[]` + item "Rede" só com `networkAccess.hasAccess`;
// trocar tenant limpa site/estudo e o cache e navega para /t/{code}; site por tenant; estudo só espelha a rota.
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@giyus/ui';
import { Building2Icon, ChevronDownIcon, GlobeIcon, MapPinIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useActiveContext } from '../lib/context/useActiveContext';

const NETWORK_VALUE = '__network__';
const ALL_SITES_VALUE = '__all__';

export function ContextSelector() {
  const { t } = useTranslation('shell');
  const {
    hasNetworkAccess,
    isNetworkScope,
    context,
    site,
    studyCode,
    contexts,
    switchTenant,
    selectSite,
    goToNetwork,
  } = useActiveContext();

  const currentLabel = isNetworkScope
    ? t('context.network')
    : (context?.name ?? t('context.selectTitle'));
  const currentValue = isNetworkScope ? NETWORK_VALUE : (context?.code ?? '');

  return (
    <div
      className="flex min-w-0 max-w-full flex-wrap items-center gap-1"
      data-testid="context-selector"
    >
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            aria-label={t('context.label')}
            className="gap-2 border-border-strong font-semibold text-heading"
            data-testid="context-selector-trigger"
          >
            <span className="size-2 shrink-0 rounded-pill bg-brand" aria-hidden="true" />
            {isNetworkScope ? (
              <GlobeIcon aria-hidden="true" />
            ) : (
              <Building2Icon aria-hidden="true" />
            )}
            <span className="max-w-48 truncate">{currentLabel}</span>
            <ChevronDownIcon aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-64">
          <DropdownMenuRadioGroup
            value={currentValue}
            onValueChange={(value) => {
              if (value === NETWORK_VALUE) {
                goToNetwork();
              } else if (value !== currentValue) {
                switchTenant(value);
              }
            }}
          >
            {hasNetworkAccess && (
              <>
                <DropdownMenuRadioItem value={NETWORK_VALUE} data-testid="context-option-network">
                  <span className="flex flex-col">
                    <span>{t('context.network')}</span>
                    <span className="text-caption text-muted-foreground">
                      {t('context.networkDescription')}
                    </span>
                  </span>
                </DropdownMenuRadioItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuLabel>{t('context.tenants')}</DropdownMenuLabel>
            {contexts.map((item) => (
              <DropdownMenuRadioItem
                key={item.tenantId}
                value={item.code}
                data-testid={`context-option-${item.code}`}
              >
                <span className="flex flex-col">
                  <span>{item.name}</span>
                  <span className="text-caption text-muted-foreground">
                    {t('home.studyCount', { count: item.accessibleStudySiteCount })}
                  </span>
                </span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      {context && context.sites.length > 1 && (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('context.site')}
              data-testid="site-selector-trigger"
            >
              <MapPinIcon aria-hidden="true" />
              <span className="max-w-40 truncate">{site?.name ?? t('context.allSites')}</span>
              <ChevronDownIcon aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuRadioGroup
              value={site?.id ?? ALL_SITES_VALUE}
              onValueChange={(value) => {
                selectSite(value === ALL_SITES_VALUE ? null : value);
              }}
            >
              <DropdownMenuRadioItem value={ALL_SITES_VALUE}>
                {t('context.allSites')}
              </DropdownMenuRadioItem>
              {context.sites.map((item) => (
                <DropdownMenuRadioItem key={item.id} value={item.id}>
                  <span className="flex flex-col">
                    <span>{item.name}</span>
                    <span className="text-caption text-muted-foreground">{item.timeZoneId}</span>
                  </span>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {studyCode && (
        <span
          className="rounded-card border px-2 py-1 text-caption text-muted-foreground"
          data-testid="study-indicator"
        >
          {t('context.study')}: {studyCode}
        </span>
      )}
    </div>
  );
}
